import { useMemo } from 'react';

import { useFixedUIIntersectionWithDefaults } from './use-fixed-ui-intersection';
import { useHeaderStickyState } from './use-header-sticky-state';

export type HeaderIntersection = {
    /**
     * Padding needed on the left to avoid fixed UI elements
     */
    paddingLeft: number;
    /**
     * Padding needed on the right to avoid fixed UI elements
     */
    paddingRight: number;
    /**
     * Whether left padding should be reserved (sidebar trigger visible + header in collision zone)
     */
    shouldReserveLeft: boolean;
    /**
     * Whether right padding should be reserved (sync status visible + header in collision zone)
     */
    shouldReserveRight: boolean;
    /**
     * Whether the header is currently in the fixed UI collision zone
     */
    isInCollisionZone: boolean;
    /**
     * Whether the header is currently in sticky position (being held by CSS sticky)
     */
    isSticky: boolean;
};

export type UseHeaderIntersectionOptions = {
    /**
     * The top offset where the header becomes sticky (in pixels)
     * @default 0
     */
    stickyTop?: number;
    /**
     * Whether to always reserve space even when not in collision zone
     * Useful for headers that are always at the top
     * @default false
     */
    alwaysReserve?: boolean;
    /**
     * Whether to skip reservation entirely
     * Useful when another element (like Page.Header) already handles the fixed UI collision
     * @default false
     */
    skipReservation?: boolean;
};

/**
 * Hook to detect if a header would intersect with fixed UI elements
 * (SidebarTrigger on left, SyncStatus on right) and calculate padding values.
 *
 * Combines fixed UI visibility (from context) with header position
 * to determine when padding is needed.
 */
export function useHeaderIntersection(
    headerRef: React.RefObject<HTMLElement | null>,
    options: UseHeaderIntersectionOptions = {},
): HeaderIntersection {
    const { stickyTop = 0, alwaysReserve = false, skipReservation = false } = options;

    // Get fixed UI visibility and padding values from context
    const fixedUI = useFixedUIIntersectionWithDefaults();

    // Track if this header is in the collision zone (where fixed UI lives)
    const { isInCollisionZone, isSticky } = useHeaderStickyState(headerRef, { stickyTop });

    return useMemo(() => {
        // If skipReservation is true, never reserve space
        if (skipReservation) {
            return {
                paddingLeft: 0,
                paddingRight: 0,
                shouldReserveLeft: false,
                shouldReserveRight: false,
                isInCollisionZone,
                isSticky,
            };
        }

        // Reserve space when header is in collision zone OR when alwaysReserve is true
        const shouldReserve = isInCollisionZone || alwaysReserve;

        // Only reserve left space if sidebar trigger is visible AND header needs reservation
        const shouldReserveLeft = shouldReserve && fixedUI.isSidebarTriggerVisible;

        // Only reserve right space if sync status is visible AND header needs reservation
        const shouldReserveRight = shouldReserve && fixedUI.isSyncStatusVisible;

        return {
            paddingLeft: shouldReserveLeft ? fixedUI.paddingLeft : 0,
            paddingRight: shouldReserveRight ? fixedUI.paddingRight : 0,
            shouldReserveLeft,
            shouldReserveRight,
            isInCollisionZone,
            isSticky,
        };
    }, [isInCollisionZone, isSticky, alwaysReserve, skipReservation, fixedUI]);
}
